'use client'

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { type Locale, locales, defaultLocale } from './config'
import defaultMessages from '../../messages/de.json'

type Messages = Record<string, any>

interface I18nContextType {
  locale: Locale
  messages: Messages
  t: (key: string, params?: Record<string, string | number>) => string
  setLocale: (locale: Locale) => void
}

const I18nContext = createContext<I18nContextType>({
  locale: defaultLocale,
  messages: {},
  t: (key) => key,
  setLocale: () => {},
})

export function useTranslation() {
  return useContext(I18nContext)
}

function resolveKeyInBag(messages: Messages, key: string): string | null {
  const parts = key.split('.')
  let current: any = messages
  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part]
    } else {
      return null
    }
  }
  return typeof current === 'string' ? current : null
}

function resolveKey(messages: Messages, key: string): string {
  const inLocale = resolveKeyInBag(messages, key)
  if (inLocale !== null) return inLocale
  // r19.55.187: Automatischer DE-Fallback fuer fehlende Keys — vermeidet
  // dass neue Features (die nur in de.json existieren) als Roh-Key
  // ("features.v2.hero.title1") in anderen Sprachen erscheinen.
  const inDe = resolveKeyInBag(defaultMessages as Messages, key)
  if (inDe !== null) return inDe
  return key
}

// Cookie-Regex unterstuetzt zh-CN (Bindestrich) — sonst kuerzte \w+ auf "zh".
function getSavedLocale(): Locale {
  if (typeof window === 'undefined') return defaultLocale
  const cookieMatch = document.cookie.match(/NEXT_LOCALE=([A-Za-z0-9-]+)/)
  if (cookieMatch && locales.includes(cookieMatch[1] as Locale)) {
    return cookieMatch[1] as Locale
  }
  const stored = localStorage.getItem('fgl-locale')
  if (stored && locales.includes(stored as Locale)) {
    return stored as Locale
  }
  return defaultLocale
}

const messageCache: Partial<Record<Locale, Messages>> = { de: defaultMessages }

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(defaultLocale)
  const [messages, setMessages] = useState<Messages>(defaultMessages)

  const loadMessages = useCallback(async (loc: Locale) => {
    if (messageCache[loc]) {
      setMessages(messageCache[loc]!)
      return
    }
    try {
      const mod = await import(`../../messages/${loc}.json`)
      messageCache[loc] = mod.default
      setMessages(mod.default)
    } catch {
      if (loc !== defaultLocale) {
        const mod = await import(`../../messages/${defaultLocale}.json`)
        messageCache[defaultLocale] = mod.default
        setMessages(mod.default)
      }
    }
  }, [])

  useEffect(() => {
    const saved = getSavedLocale()
    setLocaleState(saved)
    loadMessages(saved)
  }, [loadMessages])

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale)
    localStorage.setItem('fgl-locale', newLocale)
    document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`
    document.documentElement.lang = newLocale
    loadMessages(newLocale)
    // r19.55.187: Nach Sprachwechsel harten Reload triggern, damit alle
    // Server-Side-Renderten Teile (News-Detail via resolveLocale, Legal-Pages,
    // Meta-Tags) sowie API-Antworten mit Cache (z.B. /api/news 120s TTL) in der
    // neuen Sprache neu geladen werden. Ohne Reload zeigen z.B. die News nach
    // Wechsel weiter die alte Sprache, weil useEffect deps sich nicht aendern.
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }, [loadMessages])

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    let result = resolveKey(messages, key)
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        result = result.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v))
      })
    }
    return result
  }, [messages])

  return (
    <I18nContext.Provider value={{ locale, messages, t, setLocale }}>
      {children}
    </I18nContext.Provider>
  )
}
